import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
	Form,
	Input,
	Button,
	Row,
	Col,
	DatePicker,
	Alert,
	InputNumber,
	Radio,
	message,
	Popconfirm,
	Modal,
	Select,
} from 'antd';
import moment from 'moment';
import { InvoiceFormConfig } from './types';
import { getCurrency, getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { getInvoiceDueDateBasedOnPaymentTerms } from '../../utils/InvoiceUtils';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import { DotSeparator } from '../common/FormDots';
import CostSection, { parseLoadedAmount } from '../SupplierInvoiceFormNew/CostSection';
import TaxFormFieldsNew from './TaxFormFieldsNew';
import InvoicePaymentTermComponent from '../buyer_invoice_form/InvoicePaymentTermComponent';
import FileUploader from '../file_uploader/FileUploader';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import { useParsedFieldIndicator } from './useParsedFieldIndicator';
import { WorkOrderCheckWarning } from '../invoice_work_order_check/WorkOrderCheckWarning';
import { ExclamationCircleFilled, ExclamationCircleOutlined } from '@ant-design/icons';
import { getSanitisedWorkOrder } from '../../utils/WorkOrderUtils';
import OWButton from '../ow_button/OWButton';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';

const DATE_FORMAT = 'MM-DD-YYYY';

interface InvoiceFormBaseProps {
	config: InvoiceFormConfig;
	handleCancel?: () => void;
}

const InvoiceFormBase: React.FC<InvoiceFormBaseProps> = ({ config, handleCancel }) => {
	const [form] = Form.useForm();
	// Only a project invoice renders the Service Provider picker. Its currency follows the
	// provider, so the selection has to be watched rather than read once — see the currency
	// effect below.
	const watchedSupplierFacilityId = Form.useWatch('supplierFacilityId', form);
	const editorRef = useRef(null);
	const [formDataInitialized, setFormDataInitialized] = useState(false);
	const [currency, setCurrency] = useState<any>(null);
	const [totalBeforeTax, setTotalBeforeTax] = useState(0);
	const [totalAfterTax, setTotalAfterTax] = useState(0);
	const [timeIn] = useState(TIME_IN.HOURS);
	const [taxKeys, setTaxKeys] = useState<any[]>([]);
	// The currency the tax rows currently on the form belong to — whether this effect
	// generated them or they came off the invoice. Genuinely cross-render bookkeeping: it
	// must survive between runs of the currency effect without triggering a render, and as
	// state it would be a dependency of the very effect that sets it.
	const taxRowsCurrencyId = useRef<string | null>(null);
	const [showSignatureModal, setShowSignatureModal] = useState(false);
	const [signatureName, setSignatureName] = useState('');
	const [pendingSubmit, setPendingSubmit] = useState(false);
	const [showSignatureError, setShowSignatureError] = useState(false);

	const {
		formData,
		workOrder,
		currentUser,
		history,
		redirectForwardUrl,
		creating,
		updating,
		createErrors,
		updateErrors,
		onSuccess,
		onError,
		onReset,
		validateInvoiceNumber,
		parsedDataSnapshot,
		workOrderCheck,
		rawParsedData,
		disableCosts,
		formType,
		isClientView,
	} = config;

	const { getIndicator } = useParsedFieldIndicator({
		parsedDataSnapshot,
		formInstance: form,
	});

	const totalsMismatch = useMemo(() => {
		if (!parsedDataSnapshot || !parsedDataSnapshot.invoiceTotalAfterTax) {
			return false;
		}

		const parsedTotal = parseFloat(parsedDataSnapshot.invoiceTotalAfterTax) || 0;
		const calculatedTotal = totalAfterTax;

		const difference = Math.abs(parsedTotal - calculatedTotal);
		return difference > 0.01; // Allow for small floating point differences
	}, [parsedDataSnapshot, totalAfterTax]);

	useEffect(() => {
		if (!formData) return;
		if (formDataInitialized) return;

		const initialValues: any = {
			id: formData.id,
			scope: nullSafeGet('scope', formData),
			attachments: formData.attachments || [],
		};

		// Editing only, hence the id check. The status Form.Item hardcodes
		// initialValue="pending", so without seeding the saved value an edit silently moved
		// a paid or disputed invoice back to pending. A disputed invoice shows neither radio
		// selected — the options are pending/paid — but its status survives the save.
		//
		// On a CREATE this must not run: adaptParsedInvoiceToFormData seeds status 'draft',
		// which would then win over the pending fallback and publish every AI-parsed invoice
		// as an invisible draft. The adapter sets id to null precisely so create and edit can
		// be told apart here.
		if (config.fields.status && formData.id && nullSafeGet('status', formData)) {
			initialValues.status = nullSafeGet('status', formData);
		}
		if (config.fields.title) {
			initialValues.title = nullSafeGet('title', formData);
		}
		// Only a project invoice renders these, and only editing one has anything to seed —
		// without it the selects come up empty on an existing invoice and Service Provider,
		// being required, blocks the save until it is picked again.
		if (config.fields.supplierFacilityId) {
			initialValues.supplierFacilityId = nullSafeGet('supplierFacilityId', formData);
		}
		if (config.fields.locationId) {
			initialValues.locationId = nullSafeGet('locationId', formData);
		}
		if (config.fields.invoiceNumber) {
			initialValues.invoiceNumber = nullSafeGet('invoiceNumber', formData);
		}
		if (config.fields.externalWorkOrderId) {
			initialValues.externalWorkOrderId = nullSafeGet('externalWorkOrderId', formData);
		}
		if (config.fields.invoiceDate) {
			initialValues.dateOfInvoice = formData.dateOfInvoice ? moment(formData.dateOfInvoice) : null;
		}
		if (config.fields.paymentTerms) {
			initialValues.paymentTerms = nullSafeGet('paymentTerms', formData);
		}
		if (config.fields.dueDate) {
			initialValues.dueDate = formData.dueDate ? moment(formData.dueDate) : null;
		}

		form.setFieldsValue(initialValues);

		config.lineItemSections.forEach((section) => {
			if (section.show) {
				const lineItemsFieldName = `${section.type}LineItems`;
				const totalFieldName = `${section.type}Total`;
				const totalFieldNameBeforeTax = `${section.type}TotalBeforeTax`;

				let lineItems = [];
				let total = 0;

				if (section.type === 'parts') {
					const materialItems = formData.materialLineItems || [];
					lineItems = materialItems.filter((item) => item.partId);
					total = lineItems.reduce((sum, item) => sum + Number(item.amount), 0);
				} else if (section.type === 'equipment') {
					const materialItems = formData.materialLineItems || [];
					lineItems = materialItems.filter((item) => item.equipmentId);
					total = lineItems.reduce((sum, item) => sum + Number(item.amount), 0);
				} else {
					lineItems = formData[lineItemsFieldName] || [];
					total = formData[totalFieldNameBeforeTax] || 0;
				}

				const processedLineItems = lineItems.map((item) => ({
					...item,
					amount: parseLoadedAmount(item.amount),
					hourlyRate: Number(item.hourlyRate) || 0,
					seconds: Number(item.seconds) || 0,
					numberOfTech: Number(item.numberOfTech) || 0,
					quantity: Number(item.quantity) || 0,
					unitPrice: Number(item.unitPrice) || 0,
				}));

				form.setFieldsValue({
					[lineItemsFieldName]: processedLineItems,
					[totalFieldName]: Number(total) || 0,
				});
			}
		});

		// Initialize tax
		if (formData.taxKeys) {
			setTaxKeys(formData.taxKeys);
			form.setFieldsValue({ taxKeys: formData.taxKeys });
			const taxSum = (formData.taxKeys || []).reduce((sum, k) => {
				const amt = Number(formData[`tax-${k}-amount`]) || 0;
				return sum + amt;
			}, 0);
			form.setFieldsValue({ invoiceTax: taxSum });
		} else if (formData.taxLineItems && formData.taxLineItems.length > 0) {
			const taxLineItems = formData.taxLineItems;
			const keys = taxLineItems.map((_, idx) => idx);
			setTaxKeys(keys);
			const taxFieldValues: Record<string, any> = { taxKeys: keys };
			let taxSum = 0;
			taxLineItems.forEach((lineItem, idx) => {
				const amount = Number(lineItem.amount) || 0;
				taxSum += amount;
				taxFieldValues[`tax-${idx}-taxType`] = nullSafeGet('taxType', lineItem) || 'Sales Tax';
				taxFieldValues[`tax-${idx}-amount`] = amount;
			});
			taxFieldValues.invoiceTax = taxSum;
			form.setFieldsValue(taxFieldValues);
		} else {
			// No tax line items: single tax only. Set invoiceTax so payload sends only invoiceTax.
			const invoiceTax = config.tax.disabled
				? 0
				: formData.invoiceTax != null && formData.invoiceTax !== ''
				? Number(formData.invoiceTax)
				: undefined;
			if (invoiceTax !== undefined && !Number.isNaN(invoiceTax)) {
				form.setFieldsValue({ invoiceTax });
			}
		}

		setFormDataInitialized(true);
	}, [formData, form, config, timeIn, formDataInitialized]);

	const calculateTotalCost = useCallback(() => {
		let total = 0;
		config.lineItemSections.forEach((section) => {
			if (section.show) {
				const totalFieldName = `${section.type}Total`;
				const sectionTotal = Number(form.getFieldValue(totalFieldName)) || 0;
				total += sectionTotal;
			}
		});
		return Math.round(total * 100) / 100;
	}, [config.lineItemSections, form]);

	const calculateTaxAmount = useCallback(() => {
		const taxKeys = form.getFieldValue('taxKeys') || [];
		if (taxKeys.length === 0) {
			// No tax keys - read directly from invoiceTax field
			const invoiceTax = parseFloat(form.getFieldValue('invoiceTax')) || 0;
			return Math.round(invoiceTax * 100) / 100;
		}
		const taxTotal = taxKeys.reduce((sum, key) => {
			const amount = parseFloat(form.getFieldValue(`tax-${key}-amount`)) || 0;
			return sum + amount;
		}, 0);
		return Math.round(taxTotal * 100) / 100;
	}, [form]);

	const updateTotals = useCallback(() => {
		const newTotalBeforeTax = calculateTotalCost();
		const newTaxAmount = calculateTaxAmount();
		const newTotalAfterTax = newTotalBeforeTax + newTaxAmount;

		setTotalBeforeTax(newTotalBeforeTax);
		setTotalAfterTax(newTotalAfterTax);

		form.setFieldsValue({
			invoiceTotalBeforeTax: newTotalBeforeTax,
			invoiceTax: newTaxAmount,
			invoiceTotalAfterTax: newTotalAfterTax,
		});
	}, [calculateTotalCost, calculateTaxAmount, form]);

	// Fetch currency internally or use directly provided value
	useEffect(() => {
		// Set by the cleanup below when the effect re-runs (the provider, and with it the
		// currency, changed) or the form unmounts. The in-flight tax request checks it so a
		// superseded response cannot put the old currency's rows back on the form.
		let cancelled = false;
		let fetchedCurrency = null;

		// A form that owns its Service Provider field owns its currency too: the provider is
		// what the invoice is billed in, and it can be changed here (on a project invoice
		// there is no work order to pin it). Falls through to the value the page supplied
		// until a provider is picked. The slice is the one the picker itself reads, so the
		// chosen facility is already loaded in it.
		const pickedSupplierFacility =
			config.fields.supplierFacilityId && watchedSupplierFacilityId
				? getEntityById(
						watchedSupplierFacilityId,
						nullSafeGet('entitySelects.supplierFacility.stateSlice', config),
						null
				  )
				: null;

		if (pickedSupplierFacility) {
			fetchedCurrency = getCurrency({ supplierFacility: pickedSupplierFacility });
		} else if (config.currency.value) {
			// Currency passed directly from parent — no deduction needed
			fetchedCurrency = config.currency.value;
		} else if (config.currency.fetchInternally && workOrder) {
			if (config.currency.source === 'workOrder') {
				fetchedCurrency = getCurrency({ workOrder });
			} else if (config.currency.source === 'supplierFacility') {
				fetchedCurrency = getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
			} else if (config.currency.source === 'quote') {
				// For quotes, currency comes from workOrder
				fetchedCurrency = getCurrency({ workOrder });
			}
		}

		if (fetchedCurrency) {
			setCurrency(fetchedCurrency);

			// Whatever rows are on the form on the first run belong to the currency resolved
			// then — an existing invoice's persisted tax lines included. Recording that is
			// what lets a later currency change clear them: they are no more valid under a
			// new provider's currency than generated ones are.
			if (taxRowsCurrencyId.current === null && (form.getFieldValue('taxKeys') || []).length > 0) {
				taxRowsCurrencyId.current = fetchedCurrency.id;
			}

			// Changing the service provider can change the currency mid-form, and tax rows
			// for the old one do not apply to the new one — leaving them would submit e.g.
			// Canadian tax lines against a USD provider.
			if (taxRowsCurrencyId.current !== null && taxRowsCurrencyId.current !== fetchedCurrency.id) {
				// invoiceTax goes with them: with no tax keys left, calculateTaxAmount reads
				// invoiceTax back as the tax, so leaving the old currency's sum there would
				// reinstate the very figure being cleared.
				const clearedTaxFields: Record<string, any> = { taxKeys: [], invoiceTax: 0 };
				// The rows to clear are whatever the form currently holds — no need to track
				// the keys separately.
				(form.getFieldValue('taxKeys') || []).forEach((idx) => {
					clearedTaxFields[`tax-${idx}-taxType`] = undefined;
					clearedTaxFields[`tax-${idx}-amount`] = undefined;
				});
				setTaxKeys([]);
				form.setFieldsValue(clearedTaxFields);
				taxRowsCurrencyId.current = null;
				// setFieldsValue does not fire onValuesChange, so the totals would otherwise
				// keep the old tax in both the displayed figure and the hidden
				// invoiceTotalAfterTax the payload reads. Recomputed through the one shared
				// path rather than repeating the arithmetic here.
				updateTotals();
			}

			// 1. If currency is CAD, fetch tax line items. 2. If API returns items, show line inputs.
			// 3. If parsed total exists (no line items from parser), put it in first line and rest 0.
			// Skip only when formData already has taxKeys/taxLineItems (e.g. loading existing invoice).
			const formData = config.formData || {};
			// Read from the LIVE form, not config.formData: that is the record as loaded and
			// never changes, so after a currency switch cleared the rows it still claimed a
			// tax structure existed and the new currency's rows were never fetched. Going
			// CAD -> USD -> CAD left the invoice with no CAD rows at all.
			const alreadyHasTaxLineStructure = (form.getFieldValue('taxKeys') || []).length > 0;

			if (
				fetchedCurrency.id === 'CAD' &&
				config.tax.fetchTaxLineItems &&
				!alreadyHasTaxLineStructure
			) {
				const requestedCurrencyId = fetchedCurrency.id;
				config.tax
					.fetchTaxLineItems(requestedCurrencyId)
					.then((data) => {
						// The provider — and with it the currency — may have changed while this was
						// in flight. Applying a superseded response would put the old currency's tax
						// rows back on the form.
						if (cancelled) {
							return;
						}
						if (data && data.length > 0) {
							const keys = data.map((_: any, idx: number) => idx);
							setTaxKeys(keys);
							const taxFieldValues: Record<string, any> = { taxKeys: keys };
							const parsedTotal =
								formData.invoiceTax != null && formData.invoiceTax !== ''
									? Number(formData.invoiceTax)
									: null;

							const taxBreakdown = nullSafeGet('taxBreakdown', formData) || {};
							const hasTaxBreakdown = Object.keys(taxBreakdown).length > 0;

							data.forEach((item: any, idx: number) => {
								// API may return array of strings (e.g. ["GST", "HST", "PST"]) or objects
								const taxType =
									typeof item === 'string'
										? item
										: item.taxType || item.tax_type || `Tax ${idx + 1}`;
								taxFieldValues[`tax-${idx}-taxType`] = taxType;

								if (hasTaxBreakdown) {
									// Case-insensitive lookup in taxBreakdown
									const matchedKey = Object.keys(taxBreakdown).find(
										(k) => k.toLowerCase() === taxType.toLowerCase()
									);
									taxFieldValues[`tax-${idx}-amount`] =
										matchedKey != null ? Number(taxBreakdown[matchedKey]) : 0;
								} else {
									// No taxBreakdown: put total in first line item, rest 0
									if (parsedTotal != null && !Number.isNaN(parsedTotal)) {
										taxFieldValues[`tax-${idx}-amount`] = idx === 0 ? parsedTotal : 0;
									} else {
										taxFieldValues[`tax-${idx}-amount`] =
											item.amount != null ? Number(item.amount) : 0;
									}
								}
							});

							taxFieldValues.invoiceTax = keys.reduce(
								(sum, idx) => sum + (taxFieldValues[`tax-${idx}-amount`] || 0),
								0
							);
							form.setFieldsValue(taxFieldValues);
							taxRowsCurrencyId.current = requestedCurrencyId;
							// The clear before this set the total to its pre-tax figure, and
							// setFieldsValue fires no onValuesChange — without this the restored tax
							// is missing from both the displayed total and the submitted one.
							updateTotals();
						}
					})
					.catch((err) => {
						console.error('Error fetching tax line items:', err);
					});
			}
		}

		return () => {
			cancelled = true;
		};
	}, [
		config.currency,
		config.formData,
		config.fields,
		config.entitySelects,
		watchedSupplierFacilityId,
		workOrder,
		form,
		updateTotals,
	]);

	// Handle form value changes
	const handleFormValuesChange = useCallback(() => {
		updateTotals();
	}, [updateTotals]);

	// Recalculate totals after form is initialized to ensure state matches calculated values from line items
	useEffect(() => {
		if (formDataInitialized) {
			updateTotals();
		}
	}, [formDataInitialized, updateTotals]);

	// Get scope from editor
	const getScope = useCallback(() => {
		return editorRef.current && (editorRef.current as any).getContent();
	}, []);

	const handleInvoiceNumberValidation = useCallback(
		(_rule, value): Promise<void> => {
			if (!value || !validateInvoiceNumber) {
				return Promise.resolve();
			}
			const currentIsUpdate = formData.hasOwnProperty('id') && formData.id !== undefined;
			// Normally scoped by the work order's supplier facility. A project invoice has no
			// work order, so the in-form Service Provider is the only source.
			const supplierFacilityId = config.fields.supplierFacilityId
				? form.getFieldValue('supplierFacilityId')
				: nullSafeGet('supplierFacilityId', getSanitisedWorkOrder(workOrder, isClientView));
			return validateInvoiceNumber(value, supplierFacilityId)
				.catch(() => null)
				.then((data) => {
					if (data && Array.isArray(data)) {
						const duplicateInvoice = data.find(
							(invoice) =>
								(currentIsUpdate && nullSafeGet('id', invoice) !== nullSafeGet('id', formData)) ||
								!currentIsUpdate
						);
						if (duplicateInvoice) {
							const invoiceId = nullSafeGet('id', duplicateInvoice);
							const basePath =
								formType === 'buyerInvoice'
									? '/buyer/invoices/detail/'
									: '/supplier/invoices/detail/';
							return Promise.reject(
								(
									<span>
										Error: Duplicate with{' '}
										<a
											href={basePath.concat(invoiceId, '/details')}
											target="_blank"
											rel="noreferrer"
										>
											Invoice {invoiceId}
										</a>
									</span>
								) as any
							);
						}
					}
				});
		},
		[validateInvoiceNumber, workOrder, formData, formType, config.fields, form]
	);

	// Handle form submission
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			try {
				const values = await form.validateFields();

				// Check if totals mismatch and signature is required
				if (totalsMismatch && !signatureName) {
					// Show signature modal instead of submitting
					setPendingSubmit(true);
					setShowSignatureModal(true);
					setShowSignatureError(false);
					return;
				}

				const scope = getScope();
				// If signature was provided, include it in the submission
				const submissionData = signatureName ? { ...values, signatureName } : values;

				await config.onSubmit(submissionData, form, scope);
			} catch (error) {
				if (onError) {
					onError(error);
				}
			}
		},
		[form, config, onError, getScope, signatureName, totalsMismatch]
	);

	// Handle signature and create invoice
	const handleSignAndCreate = useCallback(async () => {
		if (!signatureName || signatureName.trim() === '') {
			setShowSignatureError(true);
			message.error('Please enter your name to proceed');
			return;
		}

		try {
			setShowSignatureModal(false);
			const values = await form.validateFields();
			const scope = getScope();
			const submissionData = { ...values, signatureName };
			await config.onSubmit(submissionData, form, scope);
			setPendingSubmit(false);
		} catch (error) {
			if (onError) {
				onError(error);
			}
			setPendingSubmit(false);
		}
	}, [form, config, onError, getScope, signatureName]);

	// Handle payment term selection
	const handlePaymentTermSelect = useCallback(
		(val) => {
			const dueDate = getInvoiceDueDateBasedOnPaymentTerms(form.getFieldValue, val);
			form.setFieldsValue({
				paymentTerms: val,
				...(dueDate && { dueDate }),
			});
		},
		[form]
	);

	// Handle file attachment change
	const handleFileAttachmentChange = useCallback(
		(newAttachments) => {
			form.setFieldsValue({ attachments: newAttachments });
		},
		[form]
	);

	// Handle form reset
	const handleReset = useCallback(() => {
		// Reset form fields to initial values
		form.resetFields();

		// Reset state variables
		setTotalBeforeTax(0);
		setTotalAfterTax(0);
		setTaxKeys([]);
		setSignatureName('');
		setShowSignatureModal(false);
		setPendingSubmit(false);

		// Clear rich text editor
		if (editorRef.current) {
			(editorRef.current as any).setContent('');
		}

		// Call onReset callback if provided
		if (onReset) {
			onReset();
		}
	}, [form, onReset]);

	if (!formDataInitialized || !currency) {
		return <PageLoadingPlaceholder />;
	}

	const submitting = creating || updating;
	const errorMessagesText = (createErrors || []).concat(updateErrors || []).join('; ');
	const isUpdate = formData.hasOwnProperty('id') && formData.id !== undefined;
	// Customer-facing noun so the shared form reads correctly for quotes.
	const noun = formType === 'proposal' ? 'quote' : 'invoice';
	const nounCap = formType === 'proposal' ? 'Quote' : 'Invoice';
	const submitText = isUpdate
		? config.formType === 'proposal'
			? 'Update proposal'
			: 'Update invoice'
		: config.formType === 'proposal'
		? 'Create proposal'
		: 'Create invoice';

	// Only project invoices switch these on — every other form derives the service provider
	// and the location from its work order, so this block renders nothing for them.
	const entitySelects = config.entitySelects || {};
	const renderEntitySelect = (selectConfig, renderRecord) =>
		selectConfig ? (
			<OWAsyncSelect
				stateSlice={selectConfig.stateSlice}
				targetCollectionName={selectConfig.targetCollectionName}
				fetchMultiple={selectConfig.fetchMultiple}
				fetchData={selectConfig.fetchData}
				renderRecord={renderRecord}
				sortBy={{ sort_by: 'name', order: 'ascend' }}
				allowClear={true}
			/>
		) : null;

	return (
		<Form form={form} layout="vertical" onValuesChange={handleFormValuesChange} scrollToFirstError>
			{(config.fields.supplierFacilityId || config.fields.locationId) && (
				<Row gutter={16}>
					{config.fields.supplierFacilityId && (
						<Col span={12}>
							<Form.Item
								label="Service Provider"
								name="supplierFacilityId"
								rules={[{ required: true, message: 'This field is required.' }]}
							>
								{renderEntitySelect(entitySelects.supplierFacility, (supplierFacility) => (
									<Select.Option key={supplierFacility.id} value={supplierFacility.id}>
										{supplierFacility.name}
									</Select.Option>
								))}
							</Form.Item>
						</Col>
					)}
					{config.fields.locationId && (
						<Col span={12}>
							{/* Optional: the API accepts a project invoice without one. Setting it is what
							    keeps the invoice in location-scoped spend reporting. */}
							<Form.Item
								label="Bill to location"
								name="locationId"
								extra="Optional. Set it to include this spend in location reporting."
							>
								{renderEntitySelect(entitySelects.location, (location) => (
									<Select.Option key={location.id} value={location.id}>
										{location.name}
									</Select.Option>
								))}
							</Form.Item>
						</Col>
					)}
				</Row>
			)}

			{/* Title — pre-filled from the AI extract, and editable, because a generated
			    summary is a starting point rather than an answer. */}
			{config.fields.title && (
				<Row gutter={16}>
					<Col span={24}>
						{/* Optional: the extract usually fills it, and a row with none falls back to
						    the project's title, so an empty one is never a dead end. */}
						<Form.Item label="Title" name="title">
							<Input placeholder="What this invoice is for" maxLength={255} />
						</Form.Item>
					</Col>
				</Row>
			)}

			{/* Scope */}
			{config.fields.scope && (
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item label="Scope" name="scope">
							<OWRichTextEditor
								initialEditorState={nullSafeGet('scope', formData)}
								editorRef={editorRef}
							/>
						</Form.Item>
					</Col>
				</Row>
			)}

			{/* File Attachments (for proposals) */}
			{config.fields.fileAttachments && (
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item label="File Attachments" name="attachments">
							<FileUploader
								defaultFileList={formData.attachments || []}
								convertedImages={nullSafeGet('convertedImages', formData)}
								roleType={currentUser && currentUser.userType}
								handleUploadSuccess={handleFileAttachmentChange}
								showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
								uploaderType="dragger"
							/>
						</Form.Item>
					</Col>
				</Row>
			)}
			<Row gutter={16}>
				{config.fields.invoiceNumber && (
					<Col span={12}>
						<Form.Item
							label="Invoice #"
							name="invoiceNumber"
							validateTrigger="onBlur"
							// The duplicate check is scoped to the service provider. On a project
							// invoice that provider is a field on this form, so a number entered
							// before it was picked was checked against `undefined` — re-run the
							// check whenever it changes. A work-order invoice takes its provider
							// from the work order, where it never changes, so this is inert there.
							dependencies={config.fields.supplierFacilityId ? ['supplierFacilityId'] : undefined}
							rules={[
								{ required: true, message: 'This field is required.' },
								...(validateInvoiceNumber
									? [
											{
												validator: handleInvoiceNumberValidation,
											},
									  ]
									: []),
							]}
						>
							<Input />
						</Form.Item>
					</Col>
				)}

				{config.fields.externalWorkOrderId && (
					<Col span={12}>
						<Form.Item
							label="Work Order #"
							name="externalWorkOrderId"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<Input />
						</Form.Item>
					</Col>
				)}

				{config.fields.invoiceDate && (
					<Col span={12}>
						<Form.Item
							label="Invoice Date"
							name="dateOfInvoice"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
						</Form.Item>
					</Col>
				)}

				{config.fields.paymentTerms && (
					<Col span={12}>
						<Form.Item
							label="Payment Terms"
							name="paymentTerms"
							validateTrigger="onBlur"
							rules={[{ required: true, message: 'This field is required.' }]}
							style={{ marginBottom: 0 }}
						>
							<Input placeholder="Net 30" />
						</Form.Item>
						<div style={{ marginTop: 8, marginBottom: 16 }}>
							<InvoicePaymentTermComponent onSelect={handlePaymentTermSelect} />
						</div>
					</Col>
				)}

				{config.fields.dueDate && (
					<Col span={12}>
						<Form.Item label="Due Date" name="dueDate">
							<DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
						</Form.Item>
					</Col>
				)}

				{config.fields.status && (
					<Col span={12}>
						<Form.Item label="Has this invoice been paid?" name="status" initialValue="pending">
							<Radio.Group>
								<Radio.Button value="pending">No</Radio.Button>
								<Radio.Button value="paid">Yes</Radio.Button>
							</Radio.Group>
						</Form.Item>
					</Col>
				)}
			</Row>
			{config.lineItemSections
				.filter((section) => section.show)
				.map((section) => (
					<CostSection
						key={section.type}
						label={section.config.label}
						form={form}
						fieldKey={section.type}
						currency={currency}
						config={section.config}
						onTotalChange={updateTotals}
						parsedDataSnapshot={parsedDataSnapshot}
						disabled={disableCosts}
					/>
				))}

			<Row gutter={16}>
				<Col span={24}>
					<div className="my-6 flex flex-row items-center gap-x-4">
						<div className="h5medium whitespace-nowrap">Total Before Tax</div>
						<DotSeparator />
						<div className="flex items-center gap-2 whitespace-nowrap pl-4">
							{currency.format(totalBeforeTax.toFixed(2))}
						</div>
					</div>
				</Col>

				<Col span={24}>
					{config.tax.mode === 'single' ? (
						<div className="my-6 flex flex-row items-center gap-x-4">
							<div className="h5medium whitespace-nowrap">Tax</div>
							<DotSeparator />
							<div className="flex items-center gap-2 whitespace-nowrap pl-4">
								{config.tax.disabled ? null : getIndicator('invoiceTax')}
								<Form.Item name="invoiceTax" className="mb-0">
									<InputNumber
										min={0}
										precision={2}
										disabled={disableCosts || config.tax.disabled}
										style={{ width: 160 }}
									/>
								</Form.Item>
							</div>
							{config.tax.disabled && config.tax.disabledReason ? (
								<div className="text-gray-400">{config.tax.disabledReason}</div>
							) : null}
						</div>
					) : (
						<TaxFormFieldsNew
							form={form}
							formData={formData}
							currency={currency}
							onTaxChange={updateTotals}
							disabled={disableCosts}
							taxKeysFromParent={taxKeys}
							getIndicator={getIndicator}
						/>
					)}
				</Col>

				<Col span={24}>
					<div className="my-6 flex flex-row items-center gap-x-4">
						<div className="h5medium whitespace-nowrap">Total After Tax</div>
						<DotSeparator />
						<div className="h5bold flex items-center gap-2 whitespace-nowrap pl-4">
							{getIndicator('invoiceTotalAfterTax')}
							{currency.format(totalAfterTax.toFixed(2))}
						</div>
					</div>
				</Col>
				{workOrderCheck && (
					<Col span={24} className="mb-4">
						<WorkOrderCheckWarning
							workOrderCheck={workOrderCheck}
							parsedData={rawParsedData}
							workOrder={workOrder}
							documentLabel={formType === 'proposal' ? 'Quote' : 'Invoice'}
						/>
					</Col>
				)}
				{totalsMismatch && !signatureName && (
					<Col span={24} className="mb-4">
						<div
							className="flex flex-row items-start justify-start gap-x-3 p-3"
							style={{ backgroundColor: 'rgba(255, 77, 79, 0.10)' }}
						>
							<ExclamationCircleFilled
								style={{
									color: '#FF4D4F',
									lineHeight: '28px',
								}}
							/>
							<div className="flex flex-col gap-y-3">
								<div className="bodyregular text-character-primary">
									The {noun} total from the PDF doesn't match the total after tax here. Review line
									items to find any missing or incorrect entries.{' '}
								</div>
								<div className="bodyregular text-character-primary">
									If everything looks correct, sign to proceed.
								</div>
								<div className="flex flex-row gap-x-3">
									<Button type="primary" onClick={() => setShowSignatureModal(true)}>
										Sign to proceed
									</Button>
									<Popconfirm
										title="Are you sure you? Any unsaved changes will be lost."
										onConfirm={handleReset}
										okText="Yes"
										cancelText="No"
									>
										<Button>Unable to fix, create from scratch</Button>
									</Popconfirm>
								</div>
							</div>
						</div>
					</Col>
				)}
				{disableCosts ? (
					<Col span={24} className="mb-4">
						<div
							className="flex flex-row items-start justify-start gap-x-3 p-3"
							style={{ backgroundColor: 'rgba(255, 77, 79, 0.10)' }}
						>
							<ExclamationCircleFilled
								style={{
									color: '#FAAD14',
									lineHeight: '28px',
								}}
							/>
							<div className="flex flex-col gap-y-3">
								<div className="bodyregular text-character-primary">
									Discounts are applied to every line item. If changes need to be made, clear all
									data and create {noun} manually.
								</div>

								<div className="flex flex-row gap-x-3">
									<Popconfirm
										title="Are you sure? All changes will be lost."
										onConfirm={handleReset}
										okText="Yes"
										cancelText="No"
									>
										<Button>Clear data & create from scratch</Button>
									</Popconfirm>
								</div>
							</div>
						</div>
					</Col>
				) : null}

				{errorMessagesText && (
					<Col span={24}>
						<Form.Item label="">
							<Alert message={errorMessagesText} type="error" />
						</Form.Item>
					</Col>
				)}

				<Col span={24}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
						<Popconfirm
							title="Are you sure you want to cancel? Unsaved changes will be lost."
							onConfirm={handleCancel}
							okText="Yes"
							cancelText="No"
							disabled={submitting}
						>
							<Button loading={submitting} size="large">
								Cancel
							</Button>
						</Popconfirm>
						<OWButton
							disabled={totalsMismatch && !signatureName}
							type="primary"
							htmlType="submit"
							loading={submitting}
							onClick={handleSubmit}
							size="large"
						>
							{submitText}
						</OWButton>
					</div>
				</Col>
			</Row>

			<Modal
				title={
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<ExclamationCircleOutlined style={{ color: '#ef4444', fontSize: 24 }} />
						<span className="h5medium text-character-primary">
							Totals don't match. Sign below to create {noun}
						</span>
					</div>
				}
				visible={showSignatureModal}
				onCancel={() => {
					setShowSignatureModal(false);
					setPendingSubmit(false);
					setShowSignatureError(false);
					setSignatureName(''); // Reset signature on cancel
				}}
				footer={null}
				width={600}
			>
				<div className="bodyregular mb-4 flex flex-col text-character-primary">
					<div>The {noun} total from the PDF doesn't match the 'Total with Tax'.</div>
					<div>
						If all line items have been reviewed and are correct, sign below to create the {noun}.
					</div>
				</div>
				<Form.Item
					label=""
					validateStatus={showSignatureError && signatureName.trim() === '' ? 'error' : ''}
					help={
						showSignatureError && signatureName.trim() === ''
							? 'Please enter your name to proceed'
							: ''
					}
				>
					<Input
						placeholder="John Doe"
						value={signatureName}
						onChange={(e) => setSignatureName(e.target.value)}
						onPressEnter={handleSignAndCreate}
					/>
					<div style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>
						By entering your name, you consent to its use as your signature
					</div>
				</Form.Item>
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
					<Button
						onClick={() => {
							setShowSignatureModal(false);
							setPendingSubmit(false);
							setShowSignatureError(false);
							setSignatureName(''); // Reset signature on cancel
						}}
					>
						Cancel
					</Button>
					<Button
						type="primary"
						onClick={handleSignAndCreate}
						disabled={!signatureName || signatureName.trim() === ''}
						loading={submitting}
					>
						Sign & Create {nounCap}
					</Button>
				</div>
			</Modal>
		</Form>
	);
};

export default InvoiceFormBase;
